import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { FieldAuthProvider } from './contexts/FieldAuthContext';
import Navbar from './components/Navbar/Navbar';
import SubscriptionModal from './components/Subscription/SubscriptionModal';

// Sayfalar (Masaüstü Web Portal)
import LandingPage from './pages/LandingPage/LandingPage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import Dashboard from './pages/Dashboard/Dashboard';
import PlanningPage from './pages/Planning/PlanningPage';
import VisitsPage from './pages/Visits/VisitsPage';
import StatisticsPage from './pages/Statistics/StatisticsPage';
import SubscriptionPage from './pages/Subscription/SubscriptionPage';
import ArchivePage from './pages/Archive/ArchivePage';
import DocumentsHubPage from './pages/Documents/DocumentsHubPage';
import AnnualWorkPlanPage from './pages/Documents/AnnualWorkPlanPage';
import AnnualTrainingPlanPage from './pages/Documents/AnnualTrainingPlanPage';
import RiskAnalysisPage from './pages/Documents/RiskAnalysisPage';
import EmergencyActionPlanPage from './pages/Documents/EmergencyActionPlanPage';
import EvacuationPlanPage from './pages/Documents/EvacuationPlanPage';
import CertificatesPage from './pages/Documents/CertificatesPage';
import ModulePlaceholder from './pages/Placeholder/ModulePlaceholder';

// Sayfalar (Mobil Saha PWA)
import FieldLoginPage from './pages/field/FieldLoginPage';
import FieldLayout from './components/Field/FieldLayout';
import FieldDashboard from './pages/field/FieldDashboard';
import FieldPlanningPage from './pages/field/FieldPlanningPage';
import FieldWorkplaceDetail from './pages/field/FieldWorkplaceDetail';

import './App.css';

// Mobil Cihaz Tespiti Yardımcısı
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  const mobileRegex = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const isSmallScreen = window.innerWidth <= 768;
  return mobileRegex.test(userAgent) || isSmallScreen;
};

// Korumalı Masaüstü Rota Bileşeni
const ProtectedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading-screen">
        <div className="app-spinner"></div>
        <p>Uzman Destek Yükleniyor...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-main-layout">
      <Navbar />
      <main className="app-main-content">
        {children}
      </main>
      <SubscriptionModal />
    </div>
  );
};

// Akıllı Ana Sayfa Yönlendirici (Mobil cihaz ise doğrudan Mobil Saha PWA'ya yönlendirir)
const SmartHomeRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;

  if (isMobileDevice()) {
    return <Navigate to="/field/dashboard" replace />;
  }
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
};

// Akıllı Giriş Sayfası Yönlendirici
const SmartLoginRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;

  if (isMobileDevice()) {
    return <Navigate to="/field/login" replace />;
  }
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LoginPage />;
};

// Akıllı Dashboard Yönlendirici (Mobilde masaüstü dashboard açılmasını engeller)
const SmartDashboardRoute: React.FC = () => {
  if (isMobileDevice()) {
    return <Navigate to="/field/dashboard" replace />;
  }
  return (
    <ProtectedLayout>
      <Dashboard />
    </ProtectedLayout>
  );
};

// Masaüstü Açık Rota
const DesktopPublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <FieldAuthProvider>
        <SubscriptionProvider>
          <Router>
            <Routes>
              {/* Mobil Saha PWA Rotaları (Hafif, OSGB'siz, İşyeri Kayıtsız, Doğrudan Saha Odaklı) */}
              <Route path="/field/login" element={<FieldLoginPage />} />
              <Route path="/mobile/login" element={<Navigate to="/field/login" replace />} />
              <Route path="/m/login" element={<Navigate to="/field/login" replace />} />

              <Route path="/field" element={<FieldLayout />}>
                <Route index element={<Navigate to="/field/dashboard" replace />} />
                <Route path="dashboard" element={<FieldDashboard />} />
                <Route path="planning" element={<FieldPlanningPage />} />
                <Route path="workplace/:id" element={<FieldWorkplaceDetail />} />
              </Route>
              <Route path="/field/plan" element={<Navigate to="/field/planning" replace />} />
              <Route path="/mobile" element={<Navigate to="/field/dashboard" replace />} />

              {/* Akıllı Başlangıç ve Giriş Rotaları */}
              <Route path="/" element={<SmartHomeRoute />} />
              <Route path="/login" element={<SmartLoginRoute />} />
              <Route path="/register" element={<DesktopPublicRoute><RegisterPage /></DesktopPublicRoute>} />
              <Route path="/forgot-password" element={<DesktopPublicRoute><ForgotPasswordPage /></DesktopPublicRoute>} />
              <Route path="/reset-password" element={<DesktopPublicRoute><ResetPasswordPage /></DesktopPublicRoute>} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />

              {/* Korumalı Uzman Masaüstü Rotaları */}
              <Route path="/dashboard" element={<SmartDashboardRoute />} />
              <Route
                path="/planning"
                element={
                  <ProtectedLayout>
                    <PlanningPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/visits"
                element={
                  <ProtectedLayout>
                    <PlanningPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/statistics"
                element={
                  <ProtectedLayout>
                    <StatisticsPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedLayout>
                    <SubscriptionPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/archive"
                element={
                  <ProtectedLayout>
                    <ArchivePage />
                  </ProtectedLayout>
                }
              />

              {/* Belge Yönetimi Rotaları */}
              <Route
                path="/documents"
                element={
                  <ProtectedLayout>
                    <DocumentsHubPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/annual-work"
                element={
                  <ProtectedLayout>
                    <AnnualWorkPlanPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/annual-work-plan"
                element={
                  <ProtectedLayout>
                    <AnnualWorkPlanPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/annual-training"
                element={
                  <ProtectedLayout>
                    <AnnualTrainingPlanPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/annual-training-plan"
                element={
                  <ProtectedLayout>
                    <AnnualTrainingPlanPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/risk"
                element={
                  <ProtectedLayout>
                    <RiskAnalysisPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/emergency"
                element={
                  <ProtectedLayout>
                    <EmergencyActionPlanPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/evacuation-plan"
                element={
                  <ProtectedLayout>
                    <EvacuationPlanPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/certificates"
                element={
                  <ProtectedLayout>
                    <CertificatesPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/documents/drill"
                element={
                  <ProtectedLayout>
                    <ModulePlaceholder
                      title="Tatbikat ve Eğitim Kaydı"
                      icon="🔥"
                      description="Yangın, tahliye ve ilkyardım tatbikatlarının katılımcı listeleriyle birlikte belgelenmesi yakında kullanıma açılacaktır."
                    />
                  </ProtectedLayout>
                }
              />

              {/* Bilinmeyen Rota */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </SubscriptionProvider>
      </FieldAuthProvider>
    </AuthProvider>
  );
}

export default App;
